import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowLeft, Radio, Users } from 'lucide-react';
import { useLiveVideoStatus } from '@/hooks/useLiveVideoStatus';
import { useAudioPause } from '@/hooks/useAudioPause';
import { HlsVideoPlayer } from '@/components/HlsVideoPlayer';

/**
 * LiveVideoScreen (A5)
 *
 * - Brz polling (5s) MediaMTX status endpoint-a dok je ekran montiran.
 * - HLS.js player kad `status.live && status.stream_url` postoje.
 * - AzuraCast audio se pauzira dok smo na ovom ekranu (snimimo prethodno
 *   stanje i resume-amo ga na unmount, AKO je bio playing).
 * - Fallback prikaz "trenutno nema live stream-a" kad nije aktivan.
 */
export const LiveVideoScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { status, error } = useLiveVideoStatus({ fastPoll: true });
  useAudioPause(status.live && Boolean(status.stream_url));

  const [playerError, setPlayerError] = useState<string | null>(null);

  const sourceLabel =
    status.source === 'studio'
      ? t('liveVideo.sourceStudio')
      : status.source === 'mobile'
      ? t('liveVideo.sourceMobile')
      : '';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-primary-bg overflow-y-auto">
      <div className="absolute inset-0 bg-gradient-to-b from-red-900/30 via-primary-bg to-primary-bg pointer-events-none" />

      <div className="relative z-10 flex items-center justify-between p-4 pt-10">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="w-12 h-12 rounded-full glass flex items-center justify-center hover:bg-white/10 transition-colors"
          aria-label={t('liveVideo.back')}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-lg font-semibold">{t('liveVideo.title')}</h1>
        <div className="w-12 h-12" aria-hidden="true" />
      </div>

      {status.live && (
        <div className="relative z-10 flex items-center justify-center gap-2 px-4 pb-2 text-sm">
          <motion.span
            animate={{ opacity: [1, 0.4, 1] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-2 h-2 rounded-full bg-red-500"
          />
          <span className="text-red-400 font-semibold">LIVE</span>
          {sourceLabel && (
            <>
              <span className="text-text-secondary/60">•</span>
              <span className="text-text-secondary">{sourceLabel}</span>
            </>
          )}
          {typeof status.viewers === 'number' && status.viewers > 0 && (
            <>
              <span className="text-text-secondary/60">•</span>
              <Users className="w-3 h-3 text-text-secondary" />
              <span className="text-text-secondary">{status.viewers}</span>
            </>
          )}
        </div>
      )}

      <div className="relative z-10 px-4 pt-2">
        {status.live && status.stream_url ? (
          <div className="relative">
            <div className="rounded-2xl overflow-hidden glass aspect-video bg-black">
              <HlsVideoPlayer
                src={status.stream_url}
                autoPlay
                onError={setPlayerError}
                className="w-full h-full"
              />
            </div>
            <p className="mt-2 text-xs text-text-secondary/70 text-center sm:hidden">
              {t('liveVideo.fullscreenHint')}
            </p>
            {playerError && (
              <div className="mt-2 p-3 rounded-xl bg-red-500/10 text-sm text-red-400">
                {t('liveVideo.playerError')}: {playerError}
              </div>
            )}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="aspect-video glass rounded-2xl flex flex-col items-center justify-center text-center p-6"
          >
            <Radio className="w-12 h-12 text-red-400/70 mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {t('liveVideo.notLiveTitle')}
            </h2>
            <p className="text-text-secondary text-sm max-w-sm">
              {t('liveVideo.notLiveDescription')}
            </p>
            {error && (
              <p className="text-xs text-text-secondary/60 mt-4">
                {t('liveVideo.connectionError')}
              </p>
            )}
          </motion.div>
        )}
      </div>

      <div className="relative z-10 px-4 pt-6 pb-10">
        <div className="glass rounded-2xl p-4">
          <h3 className="text-sm font-semibold mb-2 text-text-secondary">
            {t('liveVideo.aboutTitle')}
          </h3>
          <p className="text-xs text-text-secondary/80 leading-relaxed">
            {t('liveVideo.aboutDescription')}
          </p>
        </div>
      </div>
    </div>
  );
};
